import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
    Pilcrow,
    Heading1,
    Heading2,
    Heading3,
    Bold,
    Italic,
    Underline as UnderlineIcon,
    List,
    ListOrdered,
    Link as LinkIcon,
    Unlink,
    Undo2,
    Redo2,
} from 'lucide-react';

const isManagementWebsiteDebugEnabled = () => typeof window !== 'undefined'
    && window.localStorage?.getItem('management.website.debug') === '1';

const HtmlWysiwygEditor = ({ titleMessage, value, onChange, isDisabled = false }) => {
    const [t] = useTranslation('global');
    const debugEnabled = isManagementWebsiteDebugEnabled();

    const debugLog = (...args) => {
        // if (!debugEnabled) {
        //     return;
        // }

        // eslint-disable-next-line no-console
        console.log('[HtmlWysiwygEditor]', titleMessage, ...args);
    };

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
            }),
        ],
        content: value || '',
        editorProps: {
            attributes: {
                class: 'h-full min-h-full w-full bg-white rounded-sm p-secondDegree focus:outline-none text-body leading-relaxed [&_h1]:text-h2 [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-h3 [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-h4 [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-4 [&_a]:text-secondary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1',
            },
        },
        onUpdate: ({ editor: currentEditor, transaction }) => {
            // Ignore les updates techniques (toggle editable, focus, sélection) sans changement réel de document.
            if (!transaction?.docChanged || !currentEditor.isEditable) {
                return;
            }

            debugLog('onUpdate', {
                htmlLength: currentEditor.getHTML().length,
                isDisabled,
            });
            onChange?.(currentEditor.getHTML());
        },
    });

    useEffect(() => {
        if (!editor) return;

        const currentHtml = editor.getHTML();
        const nextHtml = value || '';

        debugLog('sync value -> editor', {
            currentLength: currentHtml.length,
            nextLength: nextHtml.length,
            willSync: currentHtml !== nextHtml,
            isDisabled,
        });

        if (currentHtml !== nextHtml) {
            editor.commands.setContent(nextHtml, { emitUpdate: false });
        }
    }, [editor, value]);

    useEffect(() => {
        if (!editor) return;

        // En TipTap v3, setEditable émet un update par défaut ; on le coupe pour éviter des faux "dirty".
        editor.setEditable(!isDisabled, false);
    }, [editor, isDisabled]);

    const toolbarState = useEditorState({
        editor,
        selector: ({ editor: currentEditor }) => ({
            blockStyles: (() => {
                const blockStylesSet = new Set();
                const {
                    selection: { from, to, empty },
                    doc,
                } = currentEditor.state;

                if (empty) {
                    if (currentEditor.isActive('heading', { level: 1 })) {
                        blockStylesSet.add('h1');
                    } else if (currentEditor.isActive('heading', { level: 2 })) {
                        blockStylesSet.add('h2');
                    } else if (currentEditor.isActive('heading', { level: 3 })) {
                        blockStylesSet.add('h3');
                    } else {
                        blockStylesSet.add('paragraph');
                    }
                } else {
                    doc.nodesBetween(from, to, (node) => {
                        if (!node.isTextblock) {
                            return;
                        }

                        if (node.type.name === 'heading') {
                            const level = Number(node.attrs.level);

                            if (level === 1) {
                                blockStylesSet.add('h1');
                            } else if (level === 2) {
                                blockStylesSet.add('h2');
                            } else if (level === 3) {
                                blockStylesSet.add('h3');
                            }
                        }

                        if (node.type.name === 'paragraph') {
                            blockStylesSet.add('paragraph');
                        }
                    });

                    if (blockStylesSet.size === 0) {
                        blockStylesSet.add('paragraph');
                    }
                }

                return Array.from(blockStylesSet);
            })(),
            bold: currentEditor.isActive('bold'),
            italic: currentEditor.isActive('italic'),
            underline: currentEditor.isActive('underline'),
            bulletList: currentEditor.isActive('bulletList'),
            orderedList: currentEditor.isActive('orderedList'),
            link: currentEditor.isActive('link'),
        }),
    });

    const getToolbarButtonClass = () => 'rounded-sm';

    const isBlockStyleMixed = toolbarState.blockStyles.length > 1;
    const activeBlockStyle = toolbarState.blockStyles[0] || 'paragraph';

    const applyBlockStyle = (blockStyle) => {
        if (!editor || !blockStyle) {
            return;
        }

        if (blockStyle === 'paragraph') {
            editor.chain().focus().setParagraph().run();
            return;
        }

        const headingLevel = Number(blockStyle.replace('h', ''));

        if ([1, 2, 3].includes(headingLevel)) {
            editor.chain().focus().setHeading({ level: headingLevel }).run();
        }
    };

    const handleSingleBlockStyleChange = (nextValue) => {
        if (!nextValue) {
            return;
        }

        applyBlockStyle(nextValue);
    };

    const handleMultipleBlockStyleChange = (nextValues) => {
        const currentValues = toolbarState.blockStyles;

        if (!nextValues?.length) {
            return;
        }

        const addedValues = nextValues.filter((value) => !currentValues.includes(value));
        if (addedValues.length > 0) {
            applyBlockStyle(addedValues[addedValues.length - 1]);
            return;
        }

        const removedValues = currentValues.filter((value) => !nextValues.includes(value));
        if (removedValues.length > 0) {
            applyBlockStyle(removedValues[removedValues.length - 1]);
            return;
        }

        applyBlockStyle(nextValues[nextValues.length - 1]);
    };

    const setLink = () => {
        if (isDisabled) {
            return;
        }

        const previousUrl = editor.getAttributes('link').href || '';
        const url = window.prompt(t('management.website.editor.toolbar.linkPrompt'), previousUrl);

        if (url === null) {
            return;
        }

        if (url.trim() === '') {
            editor.chain().focus().unsetLink().run();
            return;
        }

        editor.chain().focus().setLink({ href: url }).run();
    };

    if (!editor) {
        return null;
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-firstDegree p-firstDegree">
            {/* {titleMessage && <Label message={titleMessage} size="small" fontWeight="medium" color="primaryStrong" />} */}

            <div className="flex shrink-0 flex-wrap gap-firstDegree  ">
                {isBlockStyleMixed ? (
                    <ToggleGroup
                        type="multiple"
                        value={toolbarState.blockStyles}
                        onValueChange={handleMultipleBlockStyleChange}
                        className="p-1 gap-1"
                        disabled={isDisabled}
                        // size="sm"
                    >
                        <ToggleGroupItem value="paragraph" className="rounded-sm gap-1" disabled={isDisabled}>
                            <Pilcrow size={14} />
                            <Label message="management.website.editor.toolbar.paragraph" />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="h1" className="rounded-sm gap-1" disabled={isDisabled}>
                            <Heading1 size={14} />
                            <Label message="management.website.editor.toolbar.h1" />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="h2" className="rounded-sm gap-1" disabled={isDisabled}>
                            <Heading2 size={14} />
                            <Label message="management.website.editor.toolbar.h2" />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="h3" className="rounded-sm gap-1" disabled={isDisabled}>
                            <Heading3 size={14} />
                            <Label message="management.website.editor.toolbar.h3" />
                        </ToggleGroupItem>
                    </ToggleGroup>
                ) : (
                    <ToggleGroup
                        type="single"
                        value={activeBlockStyle}
                        onValueChange={handleSingleBlockStyleChange}
                        className="p-1 gap-1 h-fit "
                        disabled={isDisabled}
                    >
                        <ToggleGroupItem value="paragraph" className="rounded-sm gap-1 h-8" disabled={isDisabled}>
                            <Pilcrow size={14} />
                            <Label message="management.website.editor.toolbar.paragraph"  />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="h1" className="rounded-sm gap-1 h-8" disabled={isDisabled}>
                            <Heading1 size={14} />
                            <Label message="management.website.editor.toolbar.h1"  />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="h2" className="rounded-sm gap-1 h-8" disabled={isDisabled}>
                            <Heading2 size={14} />
                            <Label message="management.website.editor.toolbar.h2"  />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="h3" className="rounded-sm gap-1 h-8" disabled={isDisabled}>
                            <Heading3 size={14} />
                            <Label message="management.website.editor.toolbar.h3"  />
                        </ToggleGroupItem>
                    </ToggleGroup>
                )}
                <Button
                    style={toolbarState.bold ? 'fill' : 'ghost'}
                    sizePadding="small"
                    showLeftIcon
                    leftIcon={<Bold size={16} />}
                    showText={false}
                    className={getToolbarButtonClass()}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    showToolTip
                    text="management.website.editor.toolbar.bold"
                    disabled={isDisabled}
                />
                <Button
                    style={toolbarState.italic ? 'fill' : 'ghost'}
                    sizePadding="small"
                    showLeftIcon
                    leftIcon={<Italic size={16} />}
                    showText={false}
                    className={getToolbarButtonClass()}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    showToolTip
                    text="management.website.editor.toolbar.italic"
                    disabled={isDisabled}
                />
                <Button
                    style={toolbarState.underline ? 'fill' : 'ghost'}
                    sizePadding="small"
                    showLeftIcon
                    leftIcon={<UnderlineIcon size={16} />}
                    showText={false}
                    className={getToolbarButtonClass()}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    showToolTip
                    text="management.website.editor.toolbar.underline"
                    disabled={isDisabled}
                />
                <Button
                    style={toolbarState.bulletList ? 'fill' : 'ghost'}
                    sizePadding="small"
                    showLeftIcon
                    leftIcon={<List size={16} />}
                    showText={false}
                    className={getToolbarButtonClass()}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    showToolTip
                    text="management.website.editor.toolbar.bullets"
                    disabled={isDisabled}
                />
                <Button
                    style={toolbarState.orderedList ? 'fill' : 'ghost'}
                    sizePadding="small"
                    showLeftIcon
                    leftIcon={<ListOrdered size={16} />}
                    showText={false}
                    className={getToolbarButtonClass()}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    showToolTip
                    text="management.website.editor.toolbar.numbered"
                    disabled={isDisabled}
                />
                {!toolbarState.link && (
                    <Button
                        style="ghost"
                        sizePadding="small"
                        showLeftIcon
                        leftIcon={<LinkIcon size={16} />}
                        showText
                        text="management.website.editor.toolbar.link"
                        className={getToolbarButtonClass()}
                        onClick={setLink}
                        disabled={isDisabled}
                    />
                )}
                {toolbarState.link && (
                    <Button
                        style="fill"
                        sizePadding="small"
                        showLeftIcon
                        leftIcon={<Unlink size={16} />}
                        showText
                        text="management.website.editor.toolbar.unlink"
                        className={getToolbarButtonClass()}
                        onClick={() => editor.chain().focus().unsetLink().run()}
                        disabled={isDisabled}
                    />
                )}
                <Button
                    style="ghost"
                    sizePadding="small"
                    showLeftIcon
                    leftIcon={<Undo2 size={16} />}
                    showText={false}
                    className={getToolbarButtonClass()}
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={isDisabled || !editor.can().undo()}
                    showToolTip
                    text="management.website.editor.toolbar.undo"
                />
                <Button
                    style="ghost"
                    sizePadding="small"
                    showLeftIcon
                    leftIcon={<Redo2 size={16} />}
                    showText={false}
                    className={getToolbarButtonClass()}
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={isDisabled || !editor.can().redo()}
                    showToolTip
                    text="management.website.editor.toolbar.redo"
                />
            </div>

            <EditorContent editor={editor} className={`flex-1 min-h-0 overflow-y-auto ${isDisabled ? 'opacity-90' : ''}`} />
        </div>
    );
};

export default HtmlWysiwygEditor;